<template>
	<div class="messages shadow-left" :class="{ focused: store.appVisible }">
		<ChatPanel
			:chatId="id"
			:centeredMessageId="centeredMessageId"
			:mentionChatId="mentionChatId"
			:subscriptionType="effectiveSubscriptionType"
			@loaded="onChatLoaded"
			@create-thread="openCreateThread"
			ref="chatPanel"
		/>
		<ConvertToThreadModal
			v-if="convertThreadMessage"
			:bubbleId="id"
			:sourceMessageId="
				convertThreadMessage.parentmessage_id || convertThreadMessage.id
			"
			:defaultTitle="convertThreadDefaultTitle"
			@success="onConvertThreadSuccess"
			@cancel="convertThreadMessage = null"
		/>
	</div>
</template>

<script>
import store, { getChats, getChat, getMessage } from '@/store'
import {
	redirectToLastActiveGroup,
	redirectToRecentsOrChat,
} from '@/helperMethods'
import { unsubscribeFromAllPrivateBubbleChannels } from '@/helpers/pusher'
import { storage, generateThreadTitle } from '@/helpers/utils'
import { fetchChat } from '@/api/chat'
import ChatPanel from './ChatPanel.vue'
import ConvertToThreadModal from './ConvertToThreadModal.vue'

export default {
	name: 'Chat',

	components: {
		ChatPanel,
		ConvertToThreadModal,
	},

	props: ['groupSettingsOpen'],

	emits: ['loaded'],

	data() {
		return {
			store,
			id: 0,
			centeredMessageId: null,
			convertThreadMessage: null,
		}
	},

	computed: {
		currentChat() {
			return getChat(this.id)
		},
		isThread() {
			return this.currentChat?.is_thread === true
		},
		mentionChatId() {
			return this.isThread ? this.currentChat.parent_chat_id : null
		},
		effectiveSubscriptionType() {
			return this.isThread ? 'thread' : 'main'
		},
		convertThreadDefaultTitle() {
			if (!this.convertThreadMessage) return ''
			const sourceId =
				this.convertThreadMessage.parentmessage_id ||
				this.convertThreadMessage.id
			const source = getMessage(sourceId)
			return generateThreadTitle(source?.message)
		},
	},

	watch: {
		groupSettingsOpen(val, oldVal) {
			if (oldVal && !val) {
				this.$refs.chatPanel?.updateMark()
			}
		},
	},

	created() {
		if (!['pinned', 'recents', 'chat'].includes(this.$route.params.chat)) {
			redirectToLastActiveGroup()
			return
		}
	},

	mounted() {
		if (this.store.auth.user.hasactivity) {
			unsubscribeFromAllPrivateBubbleChannels()
		}

		if (window.todesktop) {
			const groupId = storage.get('desktop-last-group-id')
			if (groupId && !window.location.href.includes(`/${groupId}`)) {
				redirectToRecentsOrChat(groupId)
			}
		}

		this.$nextTick(() => {
			this.loadChat(
				this.$route.params.id,
				this.$route.query,
				this.$route.params.threadId
			)
		})
	},

	beforeRouteUpdate(to, from, next) {
		const isSameChat = to.params.id === from.params.id
		const isSameCentered = to.query.centered === from.query.centered
		const isSameThread = to.params.threadId === from.params.threadId

		// If only the thread param changed (same chat), just open/close thread
		if (isSameChat && isSameCentered && !isSameThread) {
			if (to.params.threadId) {
				this.openThreadFromRoute(parseInt(to.params.threadId))
			} else {
				EventBus.$emit('close-thread')
			}
			return next()
		}

		if (isSameChat && isSameCentered && isSameThread) return next()
		this.loadChat(to.params.id, to.query, to.params.threadId)
		next()
	},

	beforeUnmount() {
		store.currentChatId = null
	},

	methods: {
		async loadChat(id, toQuery, threadId) {
			if (isNaN(parseInt(id))) {
				console.debug(`Load function passed bad ID ${id}`)
				redirectToLastActiveGroup()
				return
			}

			const chatId = parseInt(id)

			// Wait until the bubble list is fully loaded
			if (!store.bubbleListComplete) {
				setTimeout(() => {
					this.loadChat(id, toQuery, threadId)
				}, 100)
				return
			}

			const currentChat = getChat(chatId)
			if (!currentChat || !Object.keys(currentChat).length) {
				this.$router.push('/chat/' + getChats()[0].id)
				return
			}

			// Check for a ?centered=id deep link
			const { centered } = toQuery || this.$route.query
			if (centered) {
				this.centeredMessageId = parseInt(centered)
				const url = new URL(location)
				url.searchParams.delete('centered')
				history.replaceState({}, null, url)
			} else {
				this.centeredMessageId = null
			}

			// Set the global current chat ID
			store.currentChatId = chatId
			this.id = chatId

			// If a threadId is present in the route, open that thread
			if (threadId) {
				this.openThreadFromRoute(parseInt(threadId))
			} else {
				EventBus.$emit('close-thread')
			}
		},

		async openThreadFromRoute(threadId) {
			try {
				let thread = getChat(threadId)
				if (!thread || !Object.keys(thread).length) {
					thread = await fetchChat(threadId)
				}
				EventBus.$emit('open-thread', thread)
			} catch (e) {
				console.error('Failed to load thread', threadId, e)
				EventBus.$emit('close-thread')
			}
		},

		onChatLoaded() {
			this.$emit('loaded')
		},

		openCreateThread(message) {
			if (message.parentmessage_id || message.isParent) {
				this.convertThreadMessage = message
			} else {
				EventBus.$emit('open-thread', {
					id: null,
					displayname: 'New Thread',
					source_message: message,
					parentChatId: this.id,
				})
			}
		},

		onConvertThreadSuccess(thread) {
			this.convertThreadMessage = null
			EventBus.$emit('open-thread', thread)
		},

		unload() {
			this.$refs.chatPanel?.unload()
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.messages {
	grid-area: main;
	overflow-y: hidden;
	flex: 1;
	display: flex;
	flex-direction: column;
}
</style>
